let modelViewerLoader;
const DIMENSION_VIEW_ALIGNMENT_LIMIT = Math.cos(15 * Math.PI / 180);

class NcProduct3d extends HTMLElement {
  #dialog;
  #stage;
  #message;
  #hotspots;
  #hotspotList;
  #canvas;
  #infoCard;
  #infoTitle;
  #infoDescription;
  #connector;
  #connectorLine;
  #dimensionLayer;
  #dimensions;
  #dimensionValues;
  #productPicker;
  #productSearch;
  #productList;
  #productEmpty;
  #viewer;
  #config;
  #catalogue;
  #catalogueProducts = [];
  #activeProduct;
  #attemptedProduct;
  #nativeSourceCache = new Map();
  #abortController;
  #taskController;
  #session = 0;
  #state = 'closed';
  #lastTrigger;
  #retryCount = 0;
  #activeHotspot;
  #overlayFrame = 0;
  #resizeObserver;
  #technicalView;
  #technicalModulePromise;
  #technicalModuleRetries = 0;
  #initialExteriorPreset;
  #framingSize;
  #guidedIndex = -1;
  #guidedRun = 0;
  #guidedDimensionsWereVisible = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    this.#stage = this.querySelector('[data-3d-stage]');
    this.#message = this.querySelector('[data-3d-message]');
    this.#hotspots = this.querySelector('[data-3d-hotspots]');
    this.#hotspotList = this.querySelector('[data-3d-hotspot-list]');
    this.#canvas = this.querySelector('[data-3d-canvas]');
    this.#infoCard = this.querySelector('[data-3d-info-card]');
    this.#infoTitle = this.querySelector('[data-3d-info-title]');
    this.#infoDescription = this.querySelector('[data-3d-info-description]');
    this.#connector = this.querySelector('[data-3d-connector]');
    this.#connectorLine = this.querySelector('[data-3d-connector-line]');
    this.#dimensionLayer = this.querySelector('[data-3d-dimension-layer]');
    this.#dimensions = this.querySelector('[data-3d-dimensions]');
    this.#dimensionValues = this.querySelector('[data-3d-dimension-values]');
    this.#productPicker = this.querySelector('[data-3d-product-picker]');
    this.#productSearch = this.querySelector('[data-3d-product-search]');
    this.#productList = this.querySelector('[data-3d-product-list]');
    this.#productEmpty = this.querySelector('[data-3d-product-empty]');
    this.#abortController = new AbortController();

    const triggers = document.querySelectorAll(`[data-3d-trigger-for="${this.id}"]`);
    for (const trigger of triggers) {
      trigger.addEventListener('click', () => this.open(trigger), { signal: this.#abortController.signal });
    }
    this.addEventListener('click', (event) => this.handleClick(event), { signal: this.#abortController.signal });
    this.#dialog.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.#abortController.signal });
    this.#dialog.addEventListener('close', () => this.handleClose(), { signal: this.#abortController.signal });
    this.#dialog.addEventListener('cancel', (event) => this.handleCancel(event), { signal: this.#abortController.signal });
    this.#productSearch?.addEventListener('input', () => this.filterProducts(), { signal: this.#abortController.signal });
    this.#stage?.addEventListener('click', (event) => {
      if (this.isProductPickerOpen) {
        this.closeProductPicker();
        return;
      }
      if (!this.hasAttribute('data-guided') && (event.target === this.#stage || event.target === this.#viewer)) this.dismissInfo();
    }, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#taskController?.abort();
    this.stopOverlayLifecycle();
    this.teardownViewer();
  }

  async open(trigger) {
    this.#lastTrigger = trigger;
    this.#retryCount = 0;
    await this.loadProduct(this.originalProduct, true);
  }

  async loadProduct(product, opening = false) {
    this.#taskController?.abort();
    this.#taskController = new AbortController();
    this.removeAttribute('data-technical-loading');
    this.closeProductPicker(false);
    this.stopOverlayLifecycle();
    this.teardownViewer();
    this.#config = undefined;
    this.#attemptedProduct = { ...product };
    this.clearMessage();
    if (!this.#dialog.open) this.#dialog.showModal();
    this.activateBackdrop();
    if (opening) this.querySelector('[data-3d-action="close"]')?.focus();
    const session = ++this.#session;
    this.setState('loading');
    this.#message.setAttribute('role', 'status');
    this.showMessage(this.translations.loading);

    try {
      const [resolved] = await Promise.all([this.loadConfig(session, product), this.loadModelViewer()]);
      if (!this.isCurrent(session)) return;
      this.#activeProduct = resolved.product;
      this.#config = this.prepareConfig(resolved.config);
      this.applyPresentation();
      this.applyCapabilities();
      this.createViewer();
      await this.waitForLoad(session);
      if (!this.isCurrent(session)) return;
      this.#initialExteriorPreset = this.initialExteriorPreset();
      await this.setCameraPreset(this.#initialExteriorPreset, true, session, true);
      if (!this.isCurrent(session)) return;
      this.renderHotspots();
      this.renderDimensions();
      await this.fitExterior(session, 'closed');
      if (!this.isCurrent(session)) return;
      this.applyExteriorConstraints();
      const framed = this.#canvas?.getBoundingClientRect();
      this.#framingSize = framed && `${framed.width}:${framed.height}`;
      this.#initialExteriorPreset = undefined;
      this.startOverlayLifecycle();
      this.clearMessage();
      this.setState('closed');
      this.showDimensions();
    } catch (error) {
      if (!this.isCurrent(session)) return;
      this.setState('error');
      this.#message.setAttribute('role', 'alert');
      this.showMessage(this.translations.error);
      this.querySelector('[data-3d-action="retry"]').hidden = false;
      console.error(`NC product 3D viewer failed to load ${this.#attemptedProduct?.handle || 'product'}`, error);
    }
  }

  handleClick(event) {
    const productButton = event.target.closest('[data-3d-product-handle]');
    if (productButton) {
      this.selectProduct(productButton.dataset['3dProductHandle']);
      return;
    }
    const dimensionGroup = event.target.closest('[data-3d-dimension-group]');
    if (dimensionGroup) {
      this.selectDimensionGroup(dimensionGroup.dataset['3dDimensionGroup']);
      return;
    }
    const button = event.target.closest('[data-3d-action]');
    if (!button) return;
    const action = button.dataset['3dAction'];
    if (action === 'close') {
      this.#dialog.close();
      return;
    }
    if (action === 'products') {
      if (this.isProductPickerOpen) this.closeProductPicker();
      else this.openProductPicker(button);
      return;
    }
    if (action === 'products-close') {
      this.closeProductPicker();
      return;
    }
    if (action === 'retry') {
      this.#retryCount += 1;
      this.loadProduct(this.#attemptedProduct || this.originalProduct);
      return;
    }
    if (action === 'dismiss-info') {
      if (this.hasAttribute('data-guided')) this.exitGuided(true);
      else this.dismissInfo(true);
      return;
    }
    this.runAction(action, button);
  }

  get originalProduct() {
    return {
      handle: this.dataset.productHandle,
      modelKey: this.dataset.modelKey,
      variantKey: this.dataset.variantKey,
    };
  }

  get locale() {
    return document.documentElement.lang?.toLowerCase().startsWith('en') ? 'en' : 'fr';
  }

  get isProductPickerOpen() {
    return Boolean(this.#productPicker && !this.#productPicker.hidden);
  }

  get productPickerBlocked() {
    return this.isPhysicalBusy || this.dataset.guidedState === 'transitioning';
  }

  openProductPicker(trigger) {
    if (this.productPickerBlocked || this.#catalogueProducts.length === 0 || !this.#productPicker) return;
    this.#productPicker.hidden = false;
    this.setAttribute('data-product-picker-open', '');
    trigger?.setAttribute('aria-expanded', 'true');
    if (this.#productSearch) this.#productSearch.value = '';
    this.filterProducts();
    this.scheduleOverlay();
    this.#productSearch?.focus();
  }

  closeProductPicker(restoreFocus = true) {
    if (!this.#productPicker) return;
    const wasOpen = !this.#productPicker.hidden;
    this.#productPicker.hidden = true;
    this.removeAttribute('data-product-picker-open');
    const trigger = this.querySelector('[data-3d-action="products"]');
    trigger?.setAttribute('aria-expanded', 'false');
    if (wasOpen && restoreFocus) trigger?.focus();
  }

  filterProducts() {
    const terms = this.normalizeProductSearch(this.#productSearch?.value || '').split(/\s+/).filter(Boolean);
    let visible = 0;
    for (const category of this.#productList?.querySelectorAll('[data-3d-product-category]') || []) {
      let categoryVisible = 0;
      for (const family of category.querySelectorAll('[data-3d-product-family]')) {
        let familyVisible = 0;
        for (const button of family.querySelectorAll('[data-3d-product-handle]')) {
          const matches = terms.every(term => button.dataset.productSearch?.includes(term));
          button.closest('.nc-product-3d__product-item')?.toggleAttribute('hidden', !matches);
          if (matches) familyVisible += 1;
        }
        const familyCount = family.querySelector('.nc-product-3d__product-family-count');
        if (familyCount) familyCount.textContent = String(familyVisible);
        family.closest('.nc-product-3d__product-family-item')?.toggleAttribute('hidden', familyVisible === 0);
        family.open = terms.length > 0 ? familyVisible > 0 : family.hasAttribute('data-current-family');
        categoryVisible += familyVisible;
      }
      const categoryCount = category.querySelector('.nc-product-3d__product-category-count');
      if (categoryCount) categoryCount.textContent = String(categoryVisible);
      category.closest('.nc-product-3d__product-category-item')?.toggleAttribute('hidden', categoryVisible === 0);
      category.open = terms.length > 0 ? categoryVisible > 0 : category.hasAttribute('data-current-category');
      visible += categoryVisible;
    }
    if (this.#productList) this.#productList.hidden = visible === 0;
    if (this.#productEmpty) this.#productEmpty.hidden = visible > 0;
  }

  normalizeProductSearch(value) {
    return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase(this.locale).trim();
  }

  legacyFamilyId(title) {
    return `legacy-${this.normalizeProductSearch(title).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
  }

  renderProductPicker(catalogue) {
    if (!this.#productList) return;
    const collator = new Intl.Collator(this.locale, { numeric: true, sensitivity: 'base' });
    this.#catalogueProducts = Object.entries(catalogue.products || {}).flatMap(([handle, mapping]) => {
      const model = catalogue.models?.[mapping.modelKey];
      const variant = model?.variants?.[mapping.variantKey];
      if (!model || !variant) return [];
      const title = model.display?.title?.[this.locale] || model.display?.title?.fr || handle;
      const format = model.display?.format?.[this.locale] || model.display?.format?.fr || '';
      const familyTitle = model.display?.family?.title?.[this.locale]
        || model.display?.family?.title?.fr
        || title;
      const familyId = model.display?.family?.id || this.legacyFamilyId(familyTitle);
      const categoryTitle = model.display?.category?.title?.[this.locale]
        || model.display?.category?.title?.fr
        || (this.locale === 'en' ? 'Containers' : 'Conteneurs');
      const categoryId = model.display?.category?.id || 'legacy-containers';
      const categoryOrder = Number(model.display?.category?.order);
      const state = mapping.variantKey === 'occasion' ? this.translations.state_used : this.translations.state_new;
      const label = [title, format, state].filter(Boolean).join(' · ');
      const formatNumber = Number.parseFloat(format.replace(',', '.').match(/\d+(?:\.\d+)?/)?.[0] || '');
      return [{
        handle,
        modelKey: mapping.modelKey,
        variantKey: mapping.variantKey,
        title,
        format,
        formatNumber: Number.isFinite(formatNumber) ? formatNumber : Number.POSITIVE_INFINITY,
        familyId,
        familyTitle,
        categoryId,
        categoryTitle,
        categoryOrder: Number.isFinite(categoryOrder) ? categoryOrder : Number.MAX_SAFE_INTEGER,
        state,
        label,
      }];
    });

    const categories = new Map();
    for (const product of this.#catalogueProducts) {
      const category = categories.get(product.categoryId) || {
        id: product.categoryId,
        title: product.categoryTitle,
        order: product.categoryOrder,
        families: new Map(),
      };
      const family = category.families.get(product.familyId) || { id: product.familyId, title: product.familyTitle, products: [] };
      family.products.push(product);
      category.families.set(product.familyId, family);
      categories.set(product.categoryId, category);
    }
    const sortedCategories = [...categories.values()].sort((first, second) => first.order - second.order
      || collator.compare(first.title, second.title));
    for (const category of sortedCategories) {
      category.sortedFamilies = [...category.families.values()].sort((first, second) => collator.compare(first.title, second.title));
      for (const family of category.sortedFamilies) {
        family.products.sort((first, second) => first.formatNumber - second.formatNumber
          || collator.compare(first.modelKey, second.modelKey)
          || Number(first.variantKey === 'occasion') - Number(second.variantKey === 'occasion')
          || collator.compare(first.label, second.label));
      }
    }
    this.#catalogueProducts = sortedCategories.flatMap(category => category.sortedFamilies.flatMap(family => family.products));

    const fragment = document.createDocumentFragment();
    for (const category of sortedCategories) {
      const categoryProducts = category.sortedFamilies.flatMap(family => family.products);
      const currentCategory = categoryProducts.some(product => product.handle === this.#activeProduct?.handle);
      const categoryItem = document.createElement('li');
      categoryItem.className = 'nc-product-3d__product-category-item';
      const categoryDetails = document.createElement('details');
      categoryDetails.className = 'nc-product-3d__product-category';
      categoryDetails.dataset['3dProductCategory'] = category.id;
      categoryDetails.toggleAttribute('data-current-category', currentCategory);
      categoryDetails.open = currentCategory;

      const categorySummary = document.createElement('summary');
      categorySummary.className = 'nc-product-3d__product-category-toggle';
      categorySummary.dataset['3dProductCategoryToggle'] = '';
      const categoryName = document.createElement('span');
      categoryName.className = 'nc-product-3d__product-category-name';
      categoryName.textContent = category.title;
      const categoryCount = document.createElement('span');
      categoryCount.className = 'nc-product-3d__product-category-count';
      categoryCount.textContent = String(categoryProducts.length);
      categorySummary.append(categoryName, categoryCount);

      const familyList = document.createElement('ul');
      familyList.className = 'nc-product-3d__product-category-list';
      familyList.setAttribute('role', 'list');
      for (const family of category.sortedFamilies) {
        const currentFamily = family.products.some(product => product.handle === this.#activeProduct?.handle);
        const familyItem = document.createElement('li');
        familyItem.className = 'nc-product-3d__product-family-item';
        const familyDetails = document.createElement('details');
        familyDetails.className = 'nc-product-3d__product-family';
        familyDetails.dataset['3dProductFamily'] = family.id;
        familyDetails.toggleAttribute('data-current-family', currentFamily);
        familyDetails.open = currentFamily;

        const familySummary = document.createElement('summary');
        familySummary.className = 'nc-product-3d__product-family-toggle';
        familySummary.dataset['3dProductFamilyToggle'] = '';
        const familyName = document.createElement('span');
        familyName.className = 'nc-product-3d__product-family-name';
        familyName.textContent = family.title;
        const familyCount = document.createElement('span');
        familyCount.className = 'nc-product-3d__product-family-count';
        familyCount.textContent = String(family.products.length);
        familySummary.append(familyName, familyCount);

        const productList = document.createElement('ul');
        productList.className = 'nc-product-3d__product-family-list';
        productList.setAttribute('role', 'list');
        for (const product of family.products) {
          const current = product.handle === this.#activeProduct?.handle;
          const item = document.createElement('li');
          item.className = 'nc-product-3d__product-item';
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'nc-product-3d__product-option';
          button.dataset['3dProductHandle'] = product.handle;
          button.dataset.productSearch = this.normalizeProductSearch(`${category.title} ${family.title} ${product.label} ${product.handle}`);
          button.setAttribute('aria-label', product.label);
          if (current) button.setAttribute('aria-current', 'true');
          button.disabled = this.productPickerBlocked;

          const name = document.createElement('span');
          name.className = 'nc-product-3d__product-name';
          name.textContent = product.title;
          const meta = document.createElement('span');
          meta.className = 'nc-product-3d__product-meta';
          meta.textContent = [product.format, product.state].filter(Boolean).join(' · ');
          button.append(name, meta);
          if (current) {
            const badge = document.createElement('span');
            badge.className = 'nc-product-3d__product-current';
            badge.textContent = this.translations.product_current;
            button.append(badge);
          }
          item.append(button);
          productList.append(item);
        }
        familyDetails.append(familySummary, productList);
        familyItem.append(familyDetails);
        familyList.append(familyItem);
      }
      categoryDetails.append(categorySummary, familyList);
      categoryItem.append(categoryDetails);
      fragment.append(categoryItem);
    }
    this.#productList.replaceChildren(fragment);
    this.filterProducts();
  }

  selectProduct(handle) {
    const product = this.#catalogueProducts.find(item => item.handle === handle);
    if (!product || this.productPickerBlocked) return;
    if (product.handle === this.#activeProduct?.handle && this.#state !== 'error') {
      this.closeProductPicker();
      return;
    }
    this.#retryCount = 0;
    this.closeProductPicker(false);
    this.querySelector('[data-3d-action="close"]')?.focus();
    this.loadProduct(product);
  }

  async runAction(action, button) {
    if (!this.#viewer) return;
    if (this.hasAttribute('data-technical-loading')) return;
    if (action === 'guided') {
      if (this.hasAttribute('data-guided')) await this.exitGuided();
      else await this.goToGuidedIndex(0);
      return;
    }
    if (action === 'guided-previous') {
      await this.goToGuidedIndex(this.#guidedIndex - 1);
      return;
    }
    if (action === 'guided-next') {
      await this.goToGuidedIndex(this.#guidedIndex + 1);
      return;
    }
    if (this.hasAttribute('data-guided')) return;
    if (['loading', 'error', 'opening', 'entering', 'reversing', 'exiting', 'closing'].includes(this.#state)
      || this.dataset.guidedState === 'transitioning') return;
    const session = this.#session;

    if (action === 'dimensions') {
      this.toggleDimensions();
      return;
    }
    if (action === 'technical') {
      await this.toggleTechnical();
      return;
    }
    if (action === 'technical-view') {
      if (!button) return;
      const view = button.dataset.technicalView;
      if (this.#technicalView?.active) {
        this.#technicalView.setView(view === '3d' ? 'iso' : view);
      }
      return;
    }
    if (action === 'technical-transparency') {
      if (!button) return;
      const pressed = button.getAttribute('aria-pressed') !== 'true';
      this.#technicalView?.setTransparent(pressed);
      return;
    }
    if (action === 'dimension-group') {
      return;
    }

    if (action === 'reset') {
      if (this.#state === 'closed') {
        this.setState('exiting');
        await this.setCamera('exterior', false, session, 'closed');
        if (!this.isCurrent(session)) return;
        await this.fitExterior(session, 'closed');
        if (!this.isCurrent(session)) return;
        this.applyExteriorConstraints();
        this.setState('closed');
        return;
      }
      const fromInterior = this.#state === 'interior';
      this.setState('exiting');
      await this.moveToExterior(session, fromInterior);
      if (!this.isCurrent(session)) return;
      await this.closeDoors(session);
      return;
    }
    if (action === 'exterior') {
      const fromInterior = this.#state === 'interior';
      this.setState('exiting');
      await this.moveToExterior(session, fromInterior);
      if (this.isCurrent(session)) this.setState('open');
      return;
    }
    if (action === 'open') {
      await this.openDoors(session);
      return;
    }
    if (action === 'close-doors') {
      await this.closeDoors(session);
      return;
    }
    if (action === 'enter') {
      await this.enter(session);
      return;
    }
    if (action === 'reverse' && this.#state === 'interior') {
      this.setState('reversing');
      await this.setCamera('interiorReverseLock', false, session);
      if (this.isCurrent(session)) this.setState('interior');
    }
  }

  async openDoors(session) {
    if (this.#state !== 'closed' || !this.#config.animations?.open) return;
    this.#technicalView?.setPose('transition');
    this.setState('opening');
    try {
      if (!this.#technicalView?.active) {
        await this.setCamera('exterior', false, session, 'open');
        if (!this.isCurrent(session)) return;
      }
      await this.playAnimation(this.#config.animations.open, session);
      if (!this.isCurrent(session)) return;
      if (this.#technicalView?.active) this.#technicalView.setPose('open');
      else await this.fitExterior(session, 'open');
      if (this.isCurrent(session)) this.setState('open');
    } catch (error) {
      if (this.isCurrent(session)) {
        this.#technicalView?.setPose('closed');
        this.setState('closed');
      }
      throw error;
    }
  }

  async closeDoors(session) {
    const fromInterior = this.#state === 'interior';
    if (fromInterior) {
      this.setState('exiting');
      await this.moveToExterior(session, fromInterior);
    }
    if (!this.isCurrent(session) || this.#state === 'closed' || !this.#config.animations?.close) return;
    this.#technicalView?.setPose('transition');
    this.setState('closing');
    await this.playAnimation(this.#config.animations.close, session);
    if (!this.isCurrent(session)) return;
    if (this.#technicalView?.active) {
      this.#technicalView.setPose('closed');
    } else if (this.hasAdaptiveMobileFraming()) {
      await this.setCamera('exterior', false, session, 'closed');
      if (!this.isCurrent(session)) return;
      await this.fitExterior(session, 'closed');
      if (!this.isCurrent(session)) return;
      this.applyExteriorConstraints();
    }
    this.setState('closed');
  }

  async enter(session) {
    if (!['closed', 'open'].includes(this.#state) || !this.#config.capabilities?.interior || !this.#config.interiorEntry) return;
    if (this.#state === 'closed') await this.openDoors(session);
    if (!this.isCurrent(session) || this.#state !== 'open') return;
    if (this.hasAttribute('data-technical')) await this.disableTechnical();
    this.setState('entering');
    this.prepareInteriorTravel();
    await this.setCamera('approach', false, session);
    if (!this.isCurrent(session)) return;
    if (this.hasEntryPath()) {
      await this.setCameraPreset(this.lockedPreset(this.#config.approach), true, session);
      if (!this.isCurrent(session)) return;
      await this.walkEntryPath(session);
      if (!this.isCurrent(session)) return;
      await this.setCamera('interiorLock', false, session);
      if (!this.isCurrent(session)) return;
      this.lockInteriorControls();
    } else {
      await this.setCamera('interiorEntry', false, session);
      if (!this.isCurrent(session)) return;
      this.lockInteriorControls();
      await this.setCamera('interiorLock', true, session);
    }
    if (!this.isCurrent(session)) return;
    this.setState('interior');
  }

  async moveToExterior(session, fromInterior) {
    if (fromInterior) {
      await this.setCamera('interiorLock', false, session);
      if (!this.isCurrent(session)) return;
      this.prepareInteriorTravel();
      if (this.hasEntryPath()) {
        await this.walkEntryPath(session, true);
        if (!this.isCurrent(session)) return;
        await this.setCameraPreset(this.lockedPreset(this.#config.approach), false, session);
        if (!this.isCurrent(session)) return;
        await this.setCamera('approach', true, session);
      } else {
        await this.setCamera('interiorEntry', false, session);
        if (!this.isCurrent(session)) return;
        await this.setCamera('approach', false, session);
      }
      if (!this.isCurrent(session)) return;
    }
    await this.setCamera('exterior', false, session, 'open');
    if (!this.isCurrent(session)) return;
    await this.fitExterior(session, 'open');
    if (!this.isCurrent(session)) return;
    this.applyExteriorConstraints();
  }

  async setCamera(name, immediate, session, exteriorPose) {
    const mobileExterior = name === 'exterior' && this.#canvas?.getBoundingClientRect().width < 600;
    const closedMobile = mobileExterior && exteriorPose === 'closed' && this.#config?.exteriorMobileClosed;
    const preset = closedMobile ? this.#config.exteriorMobileClosed : mobileExterior ? this.#config?.exteriorMobile || this.#config?.exterior : this.#config?.[name];
    if (!preset || !this.#viewer) return;
    await this.setCameraPreset(preset, immediate, session, name === 'exterior');
  }

  initialExteriorPreset() {
    const mobile = this.#canvas?.getBoundingClientRect().width < 600;
    const preset = mobile && this.#config?.exteriorMobileClosed
      ? this.#config.exteriorMobileClosed
      : mobile ? this.#config?.exteriorMobile || this.#config?.exterior : this.#config?.exterior;
    const first = (this.#config?.guidedTour || []).find(stop => stop.view === 'exterior');
    const hotspot = (this.#config?.hotspots || []).find(item => item.id === first?.hotspotId);
    const normal = hotspot?.normal;
    if (!preset || !Array.isArray(normal) || Math.hypot(normal[0], normal[2]) < 0.1) return preset;
    const faceTheta = Math.atan2(normal[0], normal[2]) * 180 / Math.PI;
    return { ...preset, target: [...preset.target], orbit: [faceTheta + 35, preset.orbit[1], preset.orbit[2]] };
  }

  async setCameraPreset(preset, immediate, session, responsiveExterior = false, minimumFov = 20) {
    if (!preset || !this.#viewer) return;
    this.#viewer.setAttribute('camera-target', `${preset.target.join('m ')}m`);
    this.#viewer.setAttribute('camera-orbit', `${preset.orbit[0]}deg ${preset.orbit[1]}deg ${preset.orbit[2]}m`);
    this.#viewer.setAttribute('min-field-of-view', `${minimumFov}deg`);
    this.#viewer.setAttribute('max-field-of-view', '80deg');
    const preserveClosedFraming = preset === this.#config?.exteriorMobileClosed;
    this.#viewer.setAttribute('field-of-view', `${responsiveExterior && !preserveClosedFraming ? this.exteriorFieldOfView() : preset.fov}deg`);
    await this.#viewer.updateComplete;
    if (!this.isCurrent(session)) return;
    if (immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#viewer.jumpCameraToGoal();
      return;
    }
    await this.waitForCamera(session);
  }

  async playAnimation(name, session) {
    if (!this.#viewer) return;
    const viewer = this.#viewer;
    viewer.animationName = name;
    await viewer.updateComplete;
    if (!this.isCurrent(session)) return;
    viewer.currentTime = 0;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      viewer.play({ repetitions: 1 });
      viewer.currentTime = viewer.duration;
      viewer.pause();
      return;
    }
    viewer.play({ repetitions: 1 });
    await this.waitForEvent(viewer, 'finished', 2500, session);
    if (!this.isCurrent(session)) return;
    // Seeking an already completed one-shot clip restarts it in model-viewer.
    // Only complete the pose if the frame loop was throttled before its endpoint.
    if (viewer.currentTime < viewer.duration - 0.01) {
      const timeScale = viewer.timeScale;
      viewer.timeScale = 1;
      viewer.currentTime = viewer.duration;
      viewer.timeScale = timeScale;
    }
    viewer.pause();
  }

  createViewer() {
    this.teardownViewer();
    const viewer = document.createElement('model-viewer');
    if (!this.#config?.src) throw new Error('3D variant source missing');
    const modelUrl = new URL(this.#config.src, window.location.href);
    if (this.#retryCount > 0) modelUrl.searchParams.set('_nc3d_retry', String(this.#retryCount));
    viewer.setAttribute('src', modelUrl.href);
    viewer.setAttribute('alt', this.#activeProduct?.label || this.querySelector('.nc-product-3d__title').textContent.trim());
    viewer.setAttribute('camera-controls', '');
    viewer.tabIndex = 0;
    viewer.setAttribute('interaction-prompt', 'none');
    viewer.setAttribute('shadow-intensity', '0.65');
    viewer.setAttribute('camera-orbit', '55deg 69deg 10.8m');
    if (this.#activeProduct?.handle === this.dataset.productHandle && this.dataset.poster) {
      viewer.setAttribute('poster', this.dataset.poster);
    }
    this.#stage.replaceChildren(viewer);
    this.#viewer = viewer;
  }

  teardownViewer() {
    ++this.#guidedRun;
    this.#guidedIndex = -1;
    this.#guidedDimensionsWereVisible = false;
    this.removeAttribute('data-guided');
    this.removeAttribute('data-guided-state');
    this.#technicalView?.destroy();
    this.#technicalView = undefined;
    this.#initialExteriorPreset = undefined;
    this.removeAttribute('data-technical-loading');
    this.removeAttribute('data-technical');
    this.removeAttribute('data-dimensions');
    this.removeAttribute('data-dimension-group');
    this.querySelector('[data-3d-technical-grid]')?.setAttribute('hidden', '');
    this.querySelector('[data-3d-technical-controls]')?.setAttribute('hidden', '');
    this.#dimensionLayer?.setAttribute('hidden', '');
    this.#dimensions?.setAttribute('hidden', '');
    this.#connector?.setAttribute('hidden', '');
    this.querySelector('[data-3d-action="technical"]')?.setAttribute('aria-pressed', 'false');
    this.querySelector('[data-3d-action="technical-transparency"]')?.setAttribute('aria-pressed', 'false');
    this.querySelector('[data-3d-action="dimensions"]')?.setAttribute('aria-pressed', 'false');
    this.selectDimensionGroup('exterior');
    this.dismissInfo();
    this.#viewer?.pause();
    this.#viewer?.remove();
    this.#viewer = undefined;
    this.#stage?.replaceChildren();
  }

  prepareInteriorTravel() {
    this.#viewer.setAttribute('disable-pan', '');
    this.#viewer.setAttribute('min-camera-orbit', 'auto auto 0m');
    this.#viewer.setAttribute('max-camera-orbit', 'auto auto 12m');
  }

  lockInteriorControls() {
    this.#viewer.setAttribute('disable-pan', '');
    this.#viewer.setAttribute('min-camera-orbit', 'auto auto 0m');
    this.#viewer.setAttribute('max-camera-orbit', 'auto auto 0m');
  }

  applyExteriorConstraints(guidedMinRadius) {
    this.#viewer.setAttribute('disable-pan', '');
    const baseMinRadius = this.#config?.cameraLimits?.exteriorMinRadius || 5.6;
    const currentTarget = this.#viewer.getCameraTarget?.();
    const referenceTarget = this.#config?.cameraLimits?.referenceTarget;
    const target = Array.isArray(currentTarget) ? currentTarget : [currentTarget?.x, currentTarget?.y, currentTarget?.z];
    const targetOffset = target.length === 3 && target.every(Number.isFinite) && Array.isArray(referenceTarget) && referenceTarget.length === 3 && referenceTarget.every(Number.isFinite)
      ? Math.hypot(...target.map((value, index) => value - referenceTarget[index]))
      : 0;
    const minRadius = guidedMinRadius ?? baseMinRadius + targetOffset;
    const maxRadius = this.#config?.cameraLimits?.exteriorMaxRadius || 14;
    this.#viewer.setAttribute('min-camera-orbit', `auto auto ${minRadius}m`);
    this.#viewer.setAttribute('max-camera-orbit', `auto auto ${maxRadius}m`);
  }

  hasEntryPath() {
    return Array.isArray(this.#config?.entryPath?.eyePoints) && this.#config.entryPath.eyePoints.length >= 2;
  }

  async walkEntryPath(session, reverse = false) {
    const points = reverse ? [...this.#config.entryPath.eyePoints].reverse() : this.#config.entryPath.eyePoints;
    for (const point of points) {
      await this.setCameraPreset({ target: point, orbit: this.#config.entryPath.orbit, fov: this.#config.entryPath.fov }, false, session);
      if (!this.isCurrent(session)) return;
    }
  }

  hasAdaptiveMobileFraming() {
    return Boolean(this.#config?.exteriorMobileClosed && this.#canvas?.getBoundingClientRect().width < 600);
  }

  async fitClosedMobile(session) {
    const exterior = this.#config?.dimensions?.groups?.exterior || [];
    if (!this.hasAdaptiveMobileFraming() || exterior.length !== 3 || !this.#viewer) return;
    const rect = this.#canvas?.getBoundingClientRect();
    if (!rect?.width) return;
    const margin = Math.min(24, rect.width * 0.08);
    const targetWidth = Math.min(rect.width * 0.8, rect.width - margin * 2);
    const source = this.#initialExteriorPreset || this.#config.exteriorMobileClosed;
    let preset = { ...source, target: [...source.target], orbit: [...source.orbit] };
    const measure = async () => {
      await this.#viewer.updateComplete;
      await new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
      return this.isCurrent(session) ? this.projectedBounds() : undefined;
    };
    let bounds = await measure();
    if (!bounds) return;
    const projectedWidth = () => bounds.right - bounds.left;
    const resize = async (requiredWidth) => {
      const minimum = this.#config.cameraLimits?.exteriorMinRadius || 0;
      const maximum = this.#config.exteriorMobile?.orbit?.[2] || Number.POSITIVE_INFINITY;
      const radius = Math.max(minimum, Math.min(maximum, preset.orbit[2] * projectedWidth() / requiredWidth));
      if (!Number.isFinite(radius) || Math.abs(radius - preset.orbit[2]) < 0.01) return false;
      preset = { ...preset, orbit: [preset.orbit[0], preset.orbit[1], radius] };
      await this.setCameraPreset(preset, true, session);
      bounds = await measure();
      return Boolean(bounds);
    };

    const occupancy = projectedWidth() / rect.width;
    if ((occupancy < 0.75 || occupancy > 0.85 || projectedWidth() > rect.width - margin * 2) && !await resize(targetWidth)) return;

    const center = async () => {
      const currentCenter = (bounds.left + bounds.right) / 2;
      const centerError = rect.width / 2 - currentCenter;
      if (Math.abs(centerError) <= 2) return true;
      const theta = this.#viewer.getCameraOrbit().theta;
      const right = [Math.cos(theta), 0, -Math.sin(theta)];
      const probeDistance = Math.max(0.1, preset.orbit[2] * 0.01);
      const beforeProbe = bounds;
      const probe = {
        ...preset,
        target: preset.target.map((value, index) => value + right[index] * probeDistance),
      };
      await this.setCameraPreset(probe, true, session);
      const probeBounds = await measure();
      if (!probeBounds) return false;
      const probeShift = (probeBounds.left + probeBounds.right - beforeProbe.left - beforeProbe.right) / 2;
      if (Math.abs(probeShift) > 0.1) {
        const targetShift = Math.max(-preset.orbit[2] * 0.15, Math.min(preset.orbit[2] * 0.15, centerError * probeDistance / probeShift));
        preset = {
          ...preset,
          target: preset.target.map((value, index) => value + right[index] * targetShift),
        };
        await this.setCameraPreset(preset, true, session);
        bounds = await measure();
        return Boolean(bounds);
      } else {
        await this.setCameraPreset(preset, true, session);
        bounds = await measure();
        return Boolean(bounds);
      }
    };
    if (!await center()) return;

    if ((bounds.left < margin || bounds.right > rect.width - margin) && !await resize(targetWidth)) return;
    await center();
  }

  async fitExterior(session, pose) {
    const rect = this.#canvas?.getBoundingClientRect();
    if (!rect || !this.#viewer) return;
    const mobile = rect.width < 600;
    const shortLandscape = rect.height < 550 && rect.width >= 600;
    if (pose === 'closed' && mobile) {
      await this.fitClosedMobile(session);
      return;
    }
    if (!mobile && !shortLandscape) return;
    await this.#viewer.updateComplete;
    await new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
    if (!this.isCurrent(session)) return;
    const bounds = this.projectedBounds();
    if (!bounds) return;
    const chromeBottom = this.canvasY('[data-3d-chrome]', 'bottom', rect);
    const dockTop = this.canvasY('[data-3d-dock]', 'top', rect);
    const availableHeight = Math.max(0, dockTop - chromeBottom - 24);
    const width = bounds.right - bounds.left;
    const height = bounds.bottom - bounds.top;
    const margin = Math.min(24, rect.width * 0.08);
    const targetWidth = Math.min(rect.width * (pose === 'open' ? (shortLandscape ? 0.62 : 0.60) : 0.78), rect.width - margin * 2);
    const targetHeight = availableHeight * (shortLandscape ? 0.82 : 0.76);
    let scale = Math.min(targetWidth / width, targetHeight / height);
    const maximumScale = pose === 'open' ? (shortLandscape ? 2.5 : 3.5) : 3;
    scale = Math.max(0.8, Math.min(maximumScale, scale));
    if (!Number.isFinite(scale) || Math.abs(scale - 1) < 0.03) return;
    const currentFov = this.exteriorFieldOfView();
    let fittedFov = 2 * Math.atan(Math.tan(currentFov * Math.PI / 360) / scale) * 180 / Math.PI;
    const base = this.#initialExteriorPreset || (mobile ? this.#config.exteriorMobile || this.#config.exterior : this.#config.exterior);
    fittedFov = Math.max(12, Math.min(65, fittedFov));
    await this.setCameraPreset({ ...base, fov: fittedFov }, true, session, false, 12);
    await new Promise(resolve => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
    if (!this.isCurrent(session)) return;
    const fittedBounds = this.projectedBounds();
    const fittedWidth = fittedBounds && fittedBounds.right - fittedBounds.left;
    if (!fittedBounds || !fittedWidth) return;
    if (fittedWidth > targetWidth || fittedBounds.left < margin || fittedBounds.right > rect.width - margin) {
      const requiredWidth = Math.min(targetWidth, rect.width - margin * 2);
      const safeFov = 2 * Math.atan(Math.tan(fittedFov * Math.PI / 360) * fittedWidth / requiredWidth) * 180 / Math.PI;
      await this.setCameraPreset({ ...base, fov: Math.max(fittedFov, Math.min(65, safeFov)) }, true, session, false, 12);
    }
  }

  exteriorFieldOfView() {
    const rect = this.#canvas?.getBoundingClientRect();
    const useClosedMobile = rect?.width < 600 && this.#config?.exteriorMobileClosed && ['loading', 'closed', 'closing'].includes(this.#state);
    const base = (useClosedMobile ? this.#config.exteriorMobileClosed : rect?.width < 600 ? this.#config?.exteriorMobile : this.#config?.exterior)?.fov || 35;
    if (!rect) return base;
    if (useClosedMobile) return base;
    // The adaptive fit changes the field of view while the collision-safe orbit stays untouched.
    return Math.max(base, rect.width < 600 ? 47 : 38);
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const focusable = [...this.#dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
      .filter((element) => this.isFocusable(element));
    if (this.#viewer && !focusable.includes(this.#viewer) && this.#viewer.cameraControls && this.isFocusable(this.#viewer)) {
      focusable.splice(1, 0, this.#viewer);
    }
    if (!focusable.length) return;

    const active = document.activeElement;
    const index = focusable.indexOf(active);
    const nextIndex = event.shiftKey
      ? (index <= 0 ? focusable.length - 1 : index - 1)
      : (index === -1 || index === focusable.length - 1 ? 0 : index + 1);
    event.preventDefault();
    if (focusable[nextIndex] === this.#viewer) {
      this.focusViewer();
      return;
    }
    focusable[nextIndex].focus();
  }

  focusViewer() {
    // Pinned model-viewer 4.3.1: focus its keyboard control, not just the host.
    const control = this.#viewer?.shadowRoot?.querySelector('.userInput');
    if (control instanceof HTMLElement) {
      control.focus();
      return;
    }
    this.#viewer?.focus();
  }

  isFocusable(element) {
    if (element.disabled || element.tabIndex < 0 || element.closest('[hidden]')) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  renderHotspots() {
    const hotspots = this.#config?.hotspots || [];
    this.#hotspotList.replaceChildren();
    this.#hotspots.hidden = hotspots.length === 0;
    for (const hotspot of hotspots) {
      const tourIndex = this.guidedTour.findIndex(stop => stop.hotspot.id === hotspot.id);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'nc-product-3d__hotspot-button';
      button.dataset.hotspotId = hotspot.id;
      button.textContent = this.translate(hotspot.labelKey);
      button.addEventListener('click', () => this.startGuidedHotspot(hotspot));
      const item = document.createElement('li');
      item.append(button);
      this.#hotspotList.append(item);

      const pin = document.createElement('button');
      pin.type = 'button';
      pin.slot = `hotspot-${hotspot.id}`;
      pin.className = 'nc-product-3d__hotspot-pin';
      pin.dataset.hotspotId = hotspot.id;
      pin.dataset.position = hotspot.position.join('m ') + 'm';
      pin.dataset.normal = hotspot.normal.join(' ');
      pin.setAttribute('aria-label', this.translate(hotspot.labelKey));
      pin.setAttribute('aria-expanded', 'false');
      pin.setAttribute('aria-controls', `${this.id}-info-title`);
      const index = document.createElement('span');
      index.className = 'nc-product-3d__hotspot-index';
      index.setAttribute('aria-hidden', 'true');
      index.textContent = String(tourIndex + 1);
      const label = document.createElement('span');
      label.className = 'nc-product-3d__hotspot-label';
      label.textContent = this.translate(hotspot.labelKey);
      pin.replaceChildren(index, label);
      pin.addEventListener('click', () => this.startGuidedHotspot(hotspot));
      this.#viewer.append(pin);
    }
    this.updateHotspots();
  }

  focusHotspot(hotspot) {
    this.#activeHotspot = hotspot;
    if (this.#infoTitle) this.#infoTitle.textContent = this.translate(hotspot.labelKey);
    if (this.#infoDescription) this.#infoDescription.textContent = this.translate(hotspot.descriptionKey);
    this.toggleAttribute('data-info-open', true);
    this.#viewer?.querySelector(`[data-hotspot-id="${hotspot.id}"]`)?.setAttribute('aria-expanded', 'true');
    this.#infoCard?.toggleAttribute('hidden', false);
    this.#connector?.toggleAttribute('hidden', false);
    const tourIndex = this.guidedTour.findIndex(stop => stop.hotspot.id === hotspot.id);
    this.querySelector('[data-3d-info-index]')?.replaceChildren(document.createTextNode(String(tourIndex + 1)));
    this.updateGuidedNavigation();
    this.scheduleOverlay();
  }

  startGuidedHotspot(hotspot) {
    if (this.isPhysicalBusy || this.dataset.guidedState === 'transitioning') return;
    const index = this.guidedTour.findIndex(stop => stop.hotspot.id === hotspot.id);
    if (index < 0) {
      this.focusHotspot(hotspot);
      return;
    }
    this.goToGuidedIndex(index);
  }

  async goToGuidedIndex(index) {
    const stop = this.guidedTour[index];
    if (!stop || !this.#viewer || this.dataset.guidedState === 'transitioning') return;
    if (this.hasAttribute('data-technical')) await this.disableTechnical();
    if (!this.#viewer) return;
    const beginning = !this.hasAttribute('data-guided');
    if (beginning) {
      this.#guidedDimensionsWereVisible = this.hasAttribute('data-dimensions');
      this.setAttribute('data-guided', '');
    }
    const run = ++this.#guidedRun;
    const session = this.#session;
    this.#guidedIndex = index;
    this.dataset.guidedState = 'transitioning';
    this.dismissInfo();
    if (this.hasAttribute('data-dimensions')) this.closeDimensions();
    this.setState(this.#state);

    const targetInterior = stop.hotspot.view === 'interior';
    const currentlyInterior = this.#state === 'interior';
    if (targetInterior && !currentlyInterior) {
      await this.enter(session);
    } else if (!targetInterior && currentlyInterior) {
      this.setState('exiting');
      await this.moveToExterior(session, true);
      if (this.isCurrent(session)) this.setState('open');
    }
    if (!this.isGuidedRun(run, session)) return;

    if (targetInterior) {
      this.lockInteriorControls();
    } else {
      this.applyExteriorConstraints();
      await this.setCamera('exterior', false, session);
      if (!this.isGuidedRun(run, session)) return;
    }
    await this.setCameraPreset(this.guidedCamera(stop), false, session);
    if (!this.isGuidedRun(run, session)) return;
    if (targetInterior) this.lockInteriorControls();
    else this.applyExteriorConstraints(stop.minRadius);
    this.dataset.guidedState = 'active';
    this.setState(this.#state);
    this.focusHotspot(stop.hotspot);
    this.querySelector('[data-3d-status]')?.replaceChildren(document.createTextNode(this.guidedProgress(index)));
  }

  guidedCamera(stop) {
    const camera = stop?.camera;
    const normal = stop?.hotspot?.normal;
    if (!camera || !Array.isArray(normal)) return camera;
    const horizontal = Math.hypot(normal[0], normal[2]);
    const rect = this.#canvas?.getBoundingClientRect();
    const shortLandscape = rect?.height < 550 && rect?.width >= 600;
    const fov = Math.max(camera.fov, shortLandscape ? 55 : rect?.width < 600 ? 42 : camera.fov);
    if (horizontal < 0.1) return { ...camera, fov };
    const theta = Math.atan2(normal[0], normal[2]) * 180 / Math.PI;
    return { ...camera, orbit: [theta, camera.orbit[1], camera.orbit[2]], fov };
  }

  async exitGuided(restoreFocus = false) {
    if (!this.hasAttribute('data-guided')) return;
    ++this.#guidedRun;
    const session = this.#session;
    const previousState = this.#state;
    const shouldRestoreDimensions = this.#guidedDimensionsWereVisible;
    this.#guidedDimensionsWereVisible = false;
    this.#guidedIndex = -1;
    this.removeAttribute('data-guided');
    this.removeAttribute('data-guided-state');
    this.dismissInfo();
    this.setState(this.#state);
    if (!this.#viewer || !this.isCurrent(session)) return;
    this.setState('exiting');
    await this.moveToExterior(session, previousState === 'interior');
    if (!this.isCurrent(session)) return;
    this.setState(previousState === 'closed' ? 'closed' : 'open');
    if (shouldRestoreDimensions) this.showDimensions();
    this.querySelector('[data-3d-status]')?.replaceChildren(document.createTextNode(this.translations.guided_off));
    if (restoreFocus) this.querySelector('[data-3d-action="guided"]')?.focus();
  }

  updateGuidedNavigation() {
    const navigation = this.querySelector('[data-3d-guided-navigation]');
    const guided = this.hasAttribute('data-guided') && this.#guidedIndex >= 0;
    if (navigation) navigation.hidden = !guided;
    const previous = this.querySelector('[data-3d-action="guided-previous"]');
    const next = this.querySelector('[data-3d-action="guided-next"]');
    const previousStop = this.guidedTour[this.#guidedIndex - 1];
    const nextStop = this.guidedTour[this.#guidedIndex + 1];
    if (previous) {
      previous.disabled = !previousStop || this.dataset.guidedState === 'transitioning';
      previous.setAttribute('aria-label', previousStop
        ? `${this.translations.guided_previous}: ${this.translate(previousStop.hotspot.labelKey)}`
        : this.translations.guided_previous);
    }
    if (next) {
      next.disabled = !nextStop || this.dataset.guidedState === 'transitioning';
      next.setAttribute('aria-label', nextStop
        ? `${this.translations.guided_next}: ${this.translate(nextStop.hotspot.labelKey)}`
        : this.translations.guided_next);
    }
    const progress = this.querySelector('[data-3d-guided-progress]');
    if (progress) progress.textContent = guided ? this.guidedProgress(this.#guidedIndex) : '';
    const guideButton = this.querySelector('[data-3d-action="guided"]');
    guideButton?.setAttribute('aria-pressed', String(this.hasAttribute('data-guided')));
  }

  guidedProgress(index) {
    return (this.translations.guided_progress || '%current% / %total%')
      .replace('%current%', String(index + 1))
      .replace('%total%', String(this.guidedTour.length));
  }

  get guidedTour() {
    const hotspots = new Map((this.#config?.hotspots || []).map(hotspot => [hotspot.id, hotspot]));
    return (this.#config?.guidedTour || [])
      .map(stop => ({ ...stop, hotspot: hotspots.get(stop.hotspotId) }))
      .filter(stop => stop.hotspot && stop.camera);
  }

  isGuidedRun(run, session) {
    return this.isCurrent(session) && this.hasAttribute('data-guided') && this.#guidedRun === run;
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isProductPickerOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.closeProductPicker();
      return;
    }
    if (event.key === 'Escape' && this.#activeHotspot) {
      event.preventDefault();
      if (this.hasAttribute('data-guided')) this.exitGuided(true);
      else this.dismissInfo(true);
      return;
    }
    this.trapFocus(event);
  }

  handleCancel(event) {
    if (this.isProductPickerOpen) {
      event.preventDefault();
      this.closeProductPicker();
      return;
    }
    if (!this.#activeHotspot || this.dataset.guidedState === 'transitioning') return;
    event.preventDefault();
    if (this.hasAttribute('data-guided')) this.exitGuided(true);
    else this.dismissInfo(true);
  }

  updateHotspots() {
    if (!this.#viewer) return;
    const interior = ['interior', 'reversing'].includes(this.#state);
    for (const hotspot of this.#config?.hotspots || []) {
      const visible = hotspot.view === (interior ? 'interior' : 'exterior');
      this.#viewer.querySelector(`[slot="hotspot-${hotspot.id}"]`)?.toggleAttribute('data-visible', visible);
      this.#hotspotList.querySelector(`[data-hotspot-id="${hotspot.id}"]`)?.closest('li').toggleAttribute('hidden', !visible);
    }
    this.updateHotspotFacing();
    this.scheduleOverlay();
  }

  updateHotspotFacing() {
    if (!this.#viewer) return;
    for (const pin of this.#viewer.querySelectorAll('.nc-product-3d__hotspot-pin')) {
      const facing = !this.isPhysicalBusy && this.dataset.guidedState !== 'transitioning'
        && pin.hasAttribute('data-visible') && Boolean(this.#viewer.queryHotspot(pin.slot)?.facingCamera);
      pin.toggleAttribute('data-facing', facing);
      pin.setAttribute('aria-hidden', String(!facing));
      pin.tabIndex = facing ? 0 : -1;
    }
  }

  startOverlayLifecycle() {
    this.stopOverlayLifecycle();
    if (!this.#viewer) return;
    this.#viewer.addEventListener('camera-change', () => this.scheduleOverlay(), { signal: this.#taskController.signal });
    this.#resizeObserver = new ResizeObserver(async () => {
      const rect = this.#canvas?.getBoundingClientRect();
      const size = rect && `${rect.width}:${rect.height}`;
      const preservesContext = !this.#activeHotspot
        && !this.hasAttribute('data-guided')
        && !this.hasAttribute('data-technical')
        && this.dataset.dimensionGroup !== 'doors';
      if (size !== this.#framingSize && ['closed', 'open'].includes(this.#state) && preservesContext) {
        this.#framingSize = size;
        const session = this.#session;
        const pose = this.#state;
        this.#initialExteriorPreset = pose === 'closed' ? this.initialExteriorPreset() : undefined;
        if (this.#initialExteriorPreset) await this.setCameraPreset(this.#initialExteriorPreset, true, session, true);
        else await this.setCamera('exterior', true, session, pose);
        if (!this.isCurrent(session)) return;
        await this.fitExterior(session, pose);
        if (!this.isCurrent(session)) return;
        this.#initialExteriorPreset = undefined;
        this.applyExteriorConstraints();
      }
      this.scheduleOverlay();
    });
    if (this.#canvas) this.#resizeObserver.observe(this.#canvas);
    this.scheduleOverlay();
  }

  stopOverlayLifecycle() {
    if (this.#overlayFrame) window.cancelAnimationFrame(this.#overlayFrame);
    this.#overlayFrame = 0;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  scheduleOverlay() {
    if (this.#overlayFrame || !this.#dialog?.open) return;
    this.#overlayFrame = window.requestAnimationFrame(() => {
      this.#overlayFrame = 0;
      if (this.#canvas) {
        const rect = this.#canvas.getBoundingClientRect();
        this.#canvas.style.setProperty('--nc-product-3d-chrome-bottom', `${this.canvasY('[data-3d-chrome]', 'bottom', rect)}px`);
      }
      this.updateHotspotFacing();
      this.updateInfoCard();
      this.drawDimensions();
    });
  }

  dismissInfo(restoreFocus = false) {
    const hotspot = this.#activeHotspot;
    this.#activeHotspot = undefined;
    this.removeAttribute('data-info-open');
    this.#viewer?.querySelector(`[data-hotspot-id="${hotspot?.id}"]`)?.setAttribute('aria-expanded', 'false');
    this.#infoCard?.toggleAttribute('hidden', true);
    this.#connector?.toggleAttribute('hidden', true);
    if (restoreFocus) this.#viewer?.querySelector(`[data-hotspot-id="${hotspot?.id}"]`)?.focus();
  }

  updateInfoCard() {
    if (!this.#activeHotspot || !this.#infoCard || !this.#canvas || !this.#viewer) return;
    const hotspot = this.#viewer.queryHotspot(`hotspot-${this.#activeHotspot.id}`);
    const point = this.canvasPoint(hotspot);
    if (!point || !hotspot.facingCamera) {
      if (this.hasAttribute('data-guided')) this.positionGuidedFallback();
      else this.dismissInfo();
      return;
    }
    this.#connector?.toggleAttribute('hidden', false);
    const card = this.#infoCard;
    const canvasRect = this.#canvas.getBoundingClientRect();
    const margin = 16;
    const bounds = this.projectedBounds();
    const sideSpace = bounds && Math.max(bounds.left - margin * 2, canvasRect.width - bounds.right - margin * 2);
    const maxWidth = canvasRect.width >= 600 && sideSpace >= 220 ? sideSpace : canvasRect.width - margin * 2;
    card.style.maxWidth = `${Math.max(0, maxWidth)}px`;
    card.style.minWidth = '0';
    const chromeBottom = this.canvasY('[data-3d-chrome]', 'bottom', canvasRect);
    const dockTop = this.canvasY('[data-3d-dock]', 'top', canvasRect);
    const minY = Math.max(margin, chromeBottom + margin);
    const maxY = Math.min(canvasRect.height - margin, dockTop - margin);
    const availableHeight = Math.max(0, maxY - minY);
    card.style.setProperty('--nc-product-3d-info-max-height', `${availableHeight}px`);
    const cardRect = card.getBoundingClientRect();
    const left = margin;
    const right = canvasRect.width - cardRect.width - margin;
    const bottom = maxY - cardRect.height;
    const centered = point.x - cardRect.width / 2;
    const placements = [
      { placement: 'top', x: right, y: minY },
      { placement: 'top', x: left, y: minY },
      { placement: 'bottom', x: centered, y: bottom },
      { placement: 'bottom', x: right, y: bottom },
      { placement: 'bottom', x: left, y: bottom },
      { placement: 'right', x: (bounds?.right || point.x) + margin, y: point.y - cardRect.height / 2 },
      { placement: 'left', x: (bounds?.left || point.x) - cardRect.width - margin, y: point.y - cardRect.height / 2 },
    ];
    const dimensionRect = this.hasAttribute('data-dimensions') && this.#dimensions?.getBoundingClientRect();
    const activePinRect = this.#viewer.querySelector(`[data-hotspot-id="${this.#activeHotspot.id}"]`)?.getBoundingClientRect();
    const pinRect = activePinRect && {
      left: activePinRect.left - canvasRect.left,
      right: activePinRect.right - canvasRect.left,
      top: activePinRect.top - canvasRect.top,
      bottom: activePinRect.bottom - canvasRect.top,
    };
    const overlap = (a, b) => Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left))
      * Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
    for (const candidate of placements) {
      candidate.x = Math.max(left, Math.min(candidate.x, right));
      candidate.y = Math.max(minY, Math.min(candidate.y, bottom));
      const rect = { left: candidate.x, right: candidate.x + cardRect.width, top: candidate.y, bottom: candidate.y + cardRect.height };
      const modelOverlap = bounds ? overlap(rect, bounds) / (cardRect.width * cardRect.height) : 0;
      const pinOverlap = pinRect ? overlap(rect, pinRect) : 0;
      const panelOverlap = dimensionRect ? overlap(rect, {
        left: dimensionRect.left - canvasRect.left, right: dimensionRect.right - canvasRect.left,
        top: dimensionRect.top - canvasRect.top, bottom: dimensionRect.bottom - canvasRect.top,
      }) : 0;
      const preferred = canvasRect.width < 600 ? candidate.placement === 'bottom' : candidate.placement === 'top';
      candidate.score = pinOverlap * 10000 + modelOverlap * 1000 + panelOverlap
        + Math.hypot(candidate.x + cardRect.width / 2 - point.x, candidate.y + cardRect.height / 2 - point.y) / canvasRect.width
        - (preferred ? 2 : 0);
    }
    const { x, y, placement } = placements.reduce((best, candidate) => candidate.score < best.score ? candidate : best);
    card.style.setProperty('--nc-product-3d-info-x', `${x}px`);
    card.style.setProperty('--nc-product-3d-info-y', `${y}px`);
    card.dataset.placement = placement;
    const current = card.getBoundingClientRect();
    const cardX = current.left - canvasRect.left;
    const cardY = current.top - canvasRect.top;
    const lineX = Math.max(cardX, Math.min(point.x, cardX + current.width));
    const lineY = Math.max(cardY, Math.min(point.y, cardY + current.height));
    this.#connectorLine?.setAttribute('x1', String(point.x));
    this.#connectorLine?.setAttribute('y1', String(point.y));
    this.#connectorLine?.setAttribute('x2', String(lineX));
    this.#connectorLine?.setAttribute('y2', String(lineY));
  }

  positionGuidedFallback() {
    if (!this.#infoCard || !this.#canvas) return;
    this.#connector?.toggleAttribute('hidden', true);
    const canvasRect = this.#canvas.getBoundingClientRect();
    const margin = 16;
    const chromeBottom = this.canvasY('[data-3d-chrome]', 'bottom', canvasRect);
    const dockTop = this.canvasY('[data-3d-dock]', 'top', canvasRect);
    const minY = Math.max(margin, chromeBottom + margin);
    const maxY = Math.min(canvasRect.height - margin, dockTop - margin);
    this.#infoCard.style.maxWidth = `${Math.max(0, canvasRect.width - margin * 2)}px`;
    this.#infoCard.style.minWidth = '0';
    this.#infoCard.style.setProperty('--nc-product-3d-info-max-height', `${Math.max(0, maxY - minY)}px`);
    const cardRect = this.#infoCard.getBoundingClientRect();
    this.#infoCard.style.setProperty('--nc-product-3d-info-x', `${margin}px`);
    this.#infoCard.style.setProperty('--nc-product-3d-info-y', `${Math.max(minY, maxY - cardRect.height)}px`);
    this.#infoCard.dataset.placement = 'bottom';
  }

  canvasY(selector, edge, canvasRect) {
    const rect = this.querySelector(selector)?.getBoundingClientRect();
    return rect ? rect[edge] - canvasRect.top : edge === 'top' ? canvasRect.height : 0;
  }

  canvasPoint(hotspot, clip = true) {
    if (!hotspot?.canvasPosition || !this.#canvas || !this.#viewer) return undefined;
    const canvasRect = this.#canvas.getBoundingClientRect();
    const viewerRect = this.#viewer.getBoundingClientRect();
    const x = viewerRect.left - canvasRect.left + hotspot.canvasPosition.x;
    const y = viewerRect.top - canvasRect.top + hotspot.canvasPosition.y;
    if (!Number.isFinite(x) || !Number.isFinite(y)) return undefined;
    if (clip && (x < 0 || y < 0 || x > canvasRect.width || y > canvasRect.height)) return undefined;
    return { x, y };
  }

  projectedBounds() {
    const points = [];
    for (let index = 0; index < 8; index += 1) {
      const point = this.canvasPoint(this.#viewer?.queryHotspot(`hotspot-bounds-${index}`), false);
      if (point) points.push(point);
    }
    if (points.length !== 8) return undefined;
    return {
      left: Math.min(...points.map(point => point.x)), right: Math.max(...points.map(point => point.x)),
      top: Math.min(...points.map(point => point.y)), bottom: Math.max(...points.map(point => point.y)),
    };
  }

  toggleDimensions() {
    if (['interior', 'reversing'].includes(this.#state)) return;
    const visible = !this.hasAttribute('data-dimensions');
    this.dismissInfo();
    if (visible) this.showDimensions();
    else this.closeDimensions();
    this.querySelector('[data-3d-status]')?.replaceChildren(document.createTextNode(visible ? this.translations.dimensions_on : this.translations.dimensions_off));
  }

  showDimensions() {
    this.setAttribute('data-dimensions', '');
    this.#dimensionLayer?.toggleAttribute('hidden', false);
    if (this.#dimensions) this.#dimensions.hidden = false;
    this.querySelector('[data-3d-action="dimensions"]')?.setAttribute('aria-pressed', 'true');
    if (!this.dataset.dimensionGroup) this.selectDimensionGroup('exterior');
    this.scheduleOverlay();
  }

  closeDimensions() {
    this.removeAttribute('data-dimensions');
    this.#dimensionLayer?.toggleAttribute('hidden', true);
    if (this.#dimensions) this.#dimensions.hidden = true;
    this.querySelector('[data-3d-action="dimensions"]')?.setAttribute('aria-pressed', 'false');
    this.scheduleOverlay();
  }

  async selectDimensionGroup(group) {
    if (!['exterior', 'interior', 'doors'].includes(group)) return;
    this.dataset.dimensionGroup = group;
    for (const button of this.querySelectorAll('[data-3d-dimension-group]')) {
      button.setAttribute('aria-pressed', String(button.dataset['3dDimensionGroup'] === group));
    }
    this.renderDimensionValues(group);
    this.scheduleOverlay();
    if (group === 'doors' && !this.isPhysicalBusy && !this.hasAttribute('data-guided') && !this.#activeHotspot) await this.focusDimensionGroup(group);
  }

  async focusDimensionGroup(group) {
    const dimensions = this.#config?.dimensions?.groups?.[group];
    if (!this.#viewer || !Array.isArray(dimensions) || !dimensions.length || ['interior', 'reversing'].includes(this.#state)) return;
    const points = dimensions.flatMap(dimension => [dimension.from, dimension.to]).filter(point => Array.isArray(point) && point.length === 3);
    if (!points.length) return;
    const target = [0, 1, 2].map(axis => points.reduce((sum, point) => sum + point[axis], 0) / points.length);
    const rect = this.#canvas?.getBoundingClientRect();
    const mobile = rect?.width < 600;
    const base = mobile ? this.#config.exteriorMobile || this.#config.exterior : this.#config.exterior;
    const fov = rect?.height < 550 ? 28 : mobile ? 34 : 32;
    const session = this.#session;
    await this.setCameraPreset({ ...base, target, fov }, false, session);
    if (this.isCurrent(session)) this.applyExteriorConstraints();
  }

  renderDimensions() {
    if (!this.#viewer || !this.#dimensionLayer) return;
    this.#dimensionLayer.replaceChildren();
    const exterior = this.#config?.dimensions?.groups?.exterior || [];
    const width = exterior.find(dimension => dimension.id === 'ext-width')?.valueM || 2.438;
    const height = exterior.find(dimension => dimension.id === 'ext-height')?.valueM || 2.591;
    const length = exterior.find(dimension => dimension.id === 'ext-length')?.valueM || 6.058;
    let index = 0;
    for (const x of [-width / 2, width / 2]) {
      for (const y of [0, height]) {
        for (const z of [-length / 2, length / 2]) this.addProjectionPoint(`bounds-${index++}`, [x, y, z]);
      }
    }
    for (const [group, dimensions] of Object.entries(this.#config?.dimensions?.groups || {})) {
      for (const dimension of dimensions) {
        for (const sx of [-1, 1]) {
          for (const sz of [-1, 1]) {
            for (const endpoint of ['from', 'to']) {
              const position = [...dimension[endpoint]];
              if (!dimension.id.includes('width') && group !== 'doors') position[0] = Math.abs(position[0]) * sx;
              if (!dimension.id.includes('length') && group !== 'doors') position[2] = Math.abs(position[2]) * sz;
              this.addProjectionPoint(`dimension-${dimension.id}-${sx}-${sz}-${endpoint}`, position);
            }
          }
        }
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        line.classList.add('nc-product-3d__dimension');
        line.dataset.dimensionId = dimension.id;
        line.dataset.dimensionGroup = group;
        line.setAttribute('aria-label', `${this.translate(dimension.labelKey)} ${this.formatMeters(dimension.valueM)}`);
        line.setAttribute('role', 'img');
        line.innerHTML = '<line class="nc-product-3d__dimension-segment"/><line class="nc-product-3d__dimension-extension"/><line class="nc-product-3d__dimension-extension"/><line class="nc-product-3d__dimension-cap"/><line class="nc-product-3d__dimension-cap"/><text></text>';
        line.querySelector('text').textContent = this.formatMeters(dimension.valueM);
        this.#dimensionLayer.append(line);
      }
    }
    this.renderDimensionValues(this.dataset.dimensionGroup || 'exterior');
  }

  addProjectionPoint(name, position) {
    const hotspot = document.createElement('span');
    hotspot.slot = `hotspot-${name}`;
    hotspot.dataset.position = position.join('m ') + 'm';
    hotspot.dataset.normal = '0 1 0';
    hotspot.setAttribute('aria-hidden', 'true');
    this.#viewer.append(hotspot);
  }

  renderDimensionValues(group) {
    if (!this.#dimensionValues) return;
    this.#dimensionValues.replaceChildren();
    for (const dimension of this.#config?.dimensions?.groups?.[group] || []) {
      const term = document.createElement('dt');
      term.textContent = this.translate(dimension.labelKey);
      const value = document.createElement('dd');
      value.textContent = this.formatMeters(dimension.valueM);
      this.#dimensionValues.append(term, value);
    }
  }

  isDimensionAlignedWithView(dimension) {
    const view = this.querySelector('[data-technical-view][aria-pressed="true"]')?.dataset.technicalView;
    if (!this.hasAttribute('data-technical') || !['front', 'side', 'top'].includes(view)) return false;
    if (![dimension?.from, dimension?.to].every(point => Array.isArray(point) && point.length === 3)) return false;
    const orbit = this.#viewer.getCameraOrbit();
    const cameraDirection = [Math.sin(orbit.phi) * Math.sin(orbit.theta), Math.cos(orbit.phi), Math.sin(orbit.phi) * Math.cos(orbit.theta)];
    const axis = dimension.to.map((value, index) => value - dimension.from[index]);
    const magnitude = Math.hypot(...axis) * Math.hypot(...cameraDirection);
    if (!magnitude) return false;
    const alignment = Math.abs(axis.reduce((sum, value, index) => sum + value * cameraDirection[index], 0)) / magnitude;
    return alignment >= DIMENSION_VIEW_ALIGNMENT_LIMIT;
  }

  drawDimensions() {
    if (!this.hasAttribute('data-dimensions') || !this.#viewer || !this.#dimensionLayer) return;
    const group = this.dataset.dimensionGroup || 'exterior';
    const bounds = this.projectedBounds();
    if (!bounds) return;
    const rect = this.#canvas.getBoundingClientRect();
    const bottomLimit = Math.min(rect.height - 12, this.canvasY('[data-3d-dock]', 'top', rect) - 12);
    const center = { x: (bounds.left + bounds.right) / 2, y: (bounds.top + bounds.bottom) / 2 };
    const theta = this.#viewer.getCameraOrbit().theta;
    const sx = Math.sin(theta) < 0 ? -1 : 1;
    const sz = Math.cos(theta) < 0 ? -1 : 1;
    const project = (id, x, z, endpoint) => this.canvasPoint(this.#viewer.queryHotspot(`hotspot-dimension-${id}-${x}-${z}-${endpoint}`), false);
    const segment = (element, from, to) => {
      element.setAttribute('x1', String(from.x)); element.setAttribute('y1', String(from.y));
      element.setAttribute('x2', String(to.x)); element.setAttribute('y2', String(to.y));
    };
    const labelRects = [];
    for (const line of this.#dimensionLayer.querySelectorAll('[data-dimension-id]')) {
      const visible = line.dataset.dimensionGroup === group && !['interior', 'reversing'].includes(this.#state);
      line.toggleAttribute('hidden', !visible);
      if (!visible) continue;
      const id = line.dataset.dimensionId;
      const dimension = this.#config?.dimensions?.groups?.[group]?.find(item => item.id === id);
      if (this.isDimensionAlignedWithView(dimension)) { line.setAttribute('hidden', ''); continue; }
      let from = project(id, sx, sz, 'from');
      let to = project(id, sx, sz, 'to');
      if (id.includes('height') && group !== 'doors') {
        // The rightmost vertical edge belongs to the silhouette, unlike the near corner.
        for (const x of [-1, 1]) {
          for (const z of [-1, 1]) {
            const a = project(id, x, z, 'from');
            const b = project(id, x, z, 'to');
            if (a && b && (!from || a.x + b.x > from.x + to.x)) { from = a; to = b; }
          }
        }
      }
      if (!from || !to) { line.setAttribute('hidden', ''); continue; }
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const distance = Math.hypot(dx, dy);
      if (distance < 3) { line.setAttribute('hidden', ''); continue; }
      const middle = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
      let normal = { x: -dy / distance, y: dx / distance };
      if (normal.x * (middle.x - center.x) + normal.y * (middle.y - center.y) < 0) normal = { x: -normal.x, y: -normal.y };
      let offset = rect.height < 550 ? 20 : rect.width < 600 ? 24 : 36;
      // Preserve a visible gutter at the viewport edge even when the user zooms in.
      for (const point of [from, to]) {
        if (normal.x > 0) offset = Math.min(offset, (rect.width - 16 - point.x) / normal.x);
        if (normal.x < 0) offset = Math.min(offset, (16 - point.x) / normal.x);
        if (normal.y > 0) offset = Math.min(offset, (bottomLimit - point.y) / normal.y);
        if (normal.y < 0) offset = Math.min(offset, (16 - point.y) / normal.y);
      }
      offset = Math.max(0, offset);
      const a = { x: from.x + normal.x * offset, y: from.y + normal.y * offset };
      const b = { x: to.x + normal.x * offset, y: to.y + normal.y * offset };
      const lines = line.querySelectorAll('line');
      segment(lines[0], a, b);
      segment(lines[1], from, { x: a.x + normal.x * 5, y: a.y + normal.y * 5 });
      segment(lines[2], to, { x: b.x + normal.x * 5, y: b.y + normal.y * 5 });
      segment(lines[3], { x: a.x - normal.x * 5, y: a.y - normal.y * 5 }, { x: a.x + normal.x * 5, y: a.y + normal.y * 5 });
      segment(lines[4], { x: b.x - normal.x * 5, y: b.y - normal.y * 5 }, { x: b.x + normal.x * 5, y: b.y + normal.y * 5 });
      const text = line.querySelector('text');
      const label = { x: middle.x + normal.x * (offset + 12), y: middle.y + normal.y * (offset + 12) };
      let angle = Math.atan2(dy, dx) * 180 / Math.PI;
      if (angle > 90) angle -= 180;
      if (angle < -90) angle += 180;
      text.setAttribute('x', String(label.x)); text.setAttribute('y', String(label.y));
      text.setAttribute('transform', `rotate(${angle} ${label.x} ${label.y})`);
      const textRect = text.getBoundingClientRect();
      const shiftX = Math.max(0, rect.left + 12 - textRect.left) - Math.max(0, textRect.right - rect.right + 12);
      const shiftY = Math.max(0, rect.top + 12 - textRect.top) - Math.max(0, textRect.bottom - rect.top - bottomLimit);
      if (shiftX || shiftY) text.setAttribute('transform', `translate(${shiftX} ${shiftY}) rotate(${angle} ${label.x} ${label.y})`);
      if (rect.width < 600 || rect.height < 550) {
        const overlaps = candidate => labelRects.some(previous => candidate.left < previous.right + 8
          && candidate.right > previous.left - 8 && candidate.top < previous.bottom + 8 && candidate.bottom > previous.top - 8);
        const originalTransform = text.getAttribute('transform');
        let candidate = text.getBoundingClientRect();
        if (overlaps(candidate)) {
          // Sliding along the measured edge separates labels without moving the cote.
          for (let step = 16; step <= 96 && overlaps(candidate); step += 16) {
            for (const direction of [1, -1]) {
              let x = dx / distance * step * direction;
              let y = dy / distance * step * direction;
              text.setAttribute('transform', `translate(${x} ${y}) ${originalTransform}`);
              candidate = text.getBoundingClientRect();
              x += Math.max(0, rect.left + 12 - candidate.left) - Math.max(0, candidate.right - rect.right + 12);
              y += Math.max(0, rect.top + 12 - candidate.top) - Math.max(0, candidate.bottom - rect.top - bottomLimit);
              text.setAttribute('transform', `translate(${x} ${y}) ${originalTransform}`);
              candidate = text.getBoundingClientRect();
              if (!overlaps(candidate)) break;
              text.setAttribute('transform', originalTransform);
              candidate = text.getBoundingClientRect();
            }
          }
        }
        labelRects.push(candidate);
      }
    }
  }

  formatMeters(value) {
    return `${new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value)} m`;
  }

  async toggleTechnical() {
    if (this.hasAttribute('data-technical')) {
      await this.disableTechnical();
      return;
    }
    const button = this.querySelector('[data-3d-action="technical"]');
    if (!this.#viewer?.model?.materials || !this.#config?.technical || !this.dataset.technicalModuleUrl || button?.disabled) return;
    const session = this.#session;
    const taskController = this.#taskController;
    const viewer = this.#viewer;
    const signal = taskController.signal;
    const isCurrent = () => this.isCurrent(session) && this.#taskController === taskController && this.#viewer === viewer && !signal.aborted;
    this.setAttribute('data-technical-loading', '');
    this.setState(this.#state);
    let technicalView;
    try {
      const moduleUrl = new URL(this.dataset.technicalModuleUrl, window.location.href);
      // A rejected ESM URL stays cached by the browser; retry with a fresh URL.
      if (this.#technicalModuleRetries) moduleUrl.searchParams.set('nc_3d_retry', String(this.#technicalModuleRetries));
      this.#technicalModulePromise ||= import(moduleUrl.href).catch((error) => {
        this.#technicalModuleRetries += 1;
        this.#technicalModulePromise = undefined;
        throw error;
      });
      const module = await this.#technicalModulePromise;
      if (!isCurrent()) return;
      const technicalUrl = new URL(this.#config.technical.asset, moduleUrl);
      technicalUrl.searchParams.set('v', this.#config.technical.assetSha256);
      technicalView = this.#technicalView || module.createTechnicalView({
        viewer,
        overlay: this.querySelector('[data-3d-technical-overlay]'),
        variant: { sha256: this.#config.technical.glbSha256, technicalUrl: technicalUrl.href },
        signal,
        onState: state => this.updateTechnicalControls(state),
        getInsets: () => this.technicalInsets(),
      });
      if (!this.#technicalView) this.#technicalView = technicalView;
      const enabled = await technicalView.enable({ pose: this.#state === 'closed' ? 'closed' : 'open', view: 'iso' });
      if (!isCurrent()) {
        technicalView.destroy();
        return;
      }
      if (!enabled) throw new Error('Technical view unavailable');
      this.setAttribute('data-technical', '');
      this.querySelector('[data-3d-technical-grid]')?.toggleAttribute('hidden', false);
      this.querySelector('[data-3d-technical-controls]')?.toggleAttribute('hidden', false);
      button.setAttribute('aria-pressed', 'true');
      this.clearMessage();
      this.querySelector('[data-3d-status]')?.replaceChildren(document.createTextNode(this.translations.technical_view_on));
    } catch (error) {
      if (isCurrent()) {
        technicalView?.restore();
        this.removeAttribute('data-technical');
        this.querySelector('[data-3d-technical-grid]')?.setAttribute('hidden', '');
        this.querySelector('[data-3d-technical-controls]')?.setAttribute('hidden', '');
        this.showMessage(this.translations.technical_error);
        console.error('NC product 3D technical view failed', error);
      }
    } finally {
      if (isCurrent()) {
        this.removeAttribute('data-technical-loading');
        this.setState(this.#state);
      }
    }
  }

  async disableTechnical() {
    if (!this.#technicalView?.active && !this.hasAttribute('data-technical')) return;
    this.#technicalView?.restore();
    this.removeAttribute('data-technical');
    this.querySelector('[data-3d-technical-grid]')?.setAttribute('hidden', '');
    this.querySelector('[data-3d-technical-controls]')?.setAttribute('hidden', '');
    this.querySelector('[data-3d-action="technical"]')?.setAttribute('aria-pressed', 'false');
    this.querySelector('[data-3d-action="technical-transparency"]')?.setAttribute('aria-pressed', 'false');
    const session = this.#session;
    const pose = this.#state === 'closed' ? 'closed' : 'open';
    await this.setCamera('exterior', true, session, pose);
    if (!this.isCurrent(session)) return;
    await this.fitExterior(session, pose);
    if (!this.isCurrent(session)) return;
    this.applyExteriorConstraints();
    this.querySelector('[data-3d-status]')?.replaceChildren(document.createTextNode(this.translations.technical_view_off));
  }

  updateTechnicalControls({ view = 'iso', active = false, transparent = false } = {}) {
    for (const button of this.querySelectorAll('[data-technical-view]')) {
      const requested = button.dataset.technicalView;
      button.setAttribute('aria-pressed', String(requested === (view === 'iso' ? '3d' : view)));
    }
    this.querySelector('[data-3d-action="technical-transparency"]')
      ?.setAttribute('aria-pressed', String(active && transparent));
    this.scheduleOverlay();
  }

  technicalInsets() {
    const canvasRect = this.#canvas?.getBoundingClientRect();
    if (!canvasRect) return { top: 0, right: 0, bottom: 0, left: 0 };
    const margin = 12;
    const chromeBottom = this.canvasY('[data-3d-chrome]', 'bottom', canvasRect);
    const dockTop = this.canvasY('[data-3d-dock]', 'top', canvasRect);
    return {
      top: chromeBottom + margin,
      right: margin,
      bottom: Math.max(margin, canvasRect.height - dockTop + margin),
      left: margin,
    };
  }

  async loadCatalogue(session) {
    if (this.#catalogue) return this.#catalogue;
    const response = await fetch(this.dataset.configUrl, { signal: this.#taskController.signal });
    if (!response.ok) throw new Error(`3D config HTTP ${response.status}`);
    const catalogue = await response.json();
    if (!this.isCurrent(session)) throw new DOMException('Closed', 'AbortError');
    this.#catalogue = catalogue;
    return catalogue;
  }

  async loadConfig(session, requestedProduct) {
    const catalogue = await this.loadCatalogue(session);
    if (catalogue.version >= 2) {
      const mapping = catalogue.products?.[requestedProduct.handle];
      if (!mapping) throw new Error('3D product mapping unavailable');
      if (requestedProduct.handle === this.dataset.productHandle
        && (mapping.modelKey !== this.dataset.modelKey || mapping.variantKey !== this.dataset.variantKey)) {
        throw new Error('3D product mapping mismatch');
      }
      const model = catalogue.models?.[mapping.modelKey];
      const variant = model?.variants?.[mapping.variantKey];
      if (!model || !variant) throw new Error('3D model variant unavailable');

      const title = model.display?.title?.[this.locale] || model.display?.title?.fr || requestedProduct.handle;
      const format = model.display?.format?.[this.locale] || model.display?.format?.fr || '';
      const state = mapping.variantKey === 'occasion' ? this.translations.state_used : this.translations.state_new;
      const product = {
        handle: requestedProduct.handle,
        modelKey: mapping.modelKey,
        variantKey: mapping.variantKey,
        title,
        format,
        state,
        label: [title, format, state].filter(Boolean).join(' · '),
      };
      this.#activeProduct = product;
      this.renderProductPicker(catalogue);
      this.applyPresentation({ ...model, ...variant }, product);

      let src = variant.src;
      if (!src && product.handle === this.dataset.productHandle) src = this.dataset.src;
      if (!src) src = await this.resolveNativeSource(product.handle, this.#taskController.signal);
      if (!src || !this.isCurrent(session)) throw new Error('3D model variant unavailable');
      return { catalogue, product, config: structuredClone({ ...model, ...variant, src }) };
    }

    const product = this.originalProduct;
    const config = { ...(catalogue.models?.[product.modelKey] || {}), src: this.dataset.src };
    this.#activeProduct = product;
    return { catalogue, product, config: structuredClone(config) };
  }

  async resolveNativeSource(handle, signal) {
    if (this.#nativeSourceCache.has(handle)) return this.#nativeSourceCache.get(handle);
    let nativeMediaIds = {};
    try {
      nativeMediaIds = JSON.parse(this.dataset.nativeMediaIds || '{}');
    } catch {
      nativeMediaIds = {};
    }
    const mediaId = nativeMediaIds[handle];
    if (!mediaId) throw new Error('Native 3D media mapping unavailable');
    const root = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
    const productUrl = new URL(`products/${encodeURIComponent(handle)}.js`, root);
    const response = await fetch(productUrl, { signal });
    if (!response.ok) throw new Error(`Product media HTTP ${response.status}`);
    const product = await response.json();
    const media = product.media?.find(item => String(item.id) === String(mediaId) && item.media_type === 'model');
    const source = media?.sources?.find(item => item.format?.toLowerCase() === 'glb')?.url;
    if (!source) throw new Error('Native GLB source unavailable');
    this.#nativeSourceCache.set(handle, source);
    return source;
  }

  applyPresentation(config = this.#config, product = this.#activeProduct) {
    const title = config?.display?.title?.[this.locale];
    const format = config?.display?.format?.[this.locale];
    const titleElement = this.querySelector('[data-3d-title-primary]');
    const formatElement = this.querySelector('[data-3d-title-format]');
    const stateElement = this.querySelector('[data-3d-title-state]');
    if (title && titleElement) titleElement.textContent = title;
    if (format && formatElement) {
      formatElement.textContent = format;
      formatElement.hidden = false;
    }
    if (stateElement && product?.state) stateElement.textContent = product.state;
    const accessibleLabel = product?.label || [title, format, product?.state].filter(Boolean).join(' · ');
    const sceneLabel = this.translations.scene_label?.replace('%product%', accessibleLabel);
    if (sceneLabel) this.#stage?.setAttribute('aria-label', sceneLabel);
    this.scheduleOverlay();
  }

  applyCapabilities() {
    const hasDimensions = Object.values(this.#config?.dimensions?.groups || {}).some(group => Array.isArray(group) && group.length);
    const visible = {
      open: Boolean(this.#config?.capabilities?.doors && this.#config?.animations?.open),
      'close-doors': Boolean(this.#config?.capabilities?.doors && this.#config?.animations?.close),
      enter: Boolean(this.#config?.capabilities?.interior && this.#config?.interiorEntry),
      reverse: Boolean(this.#config?.capabilities?.interior && this.#config?.interiorReverse),
      exterior: Boolean(this.#config?.capabilities?.interior),
      dimensions: hasDimensions,
      guided: this.guidedTour.length > 0,
    };
    for (const [action, enabled] of Object.entries(visible)) {
      const button = this.querySelector(`[data-3d-action="${action}"]`);
      if (button) button.hidden = !enabled;
    }
    for (const button of this.querySelectorAll('[data-3d-dimension-group]')) {
      const group = this.#config?.dimensions?.groups?.[button.dataset['3dDimensionGroup']];
      button.hidden = !Array.isArray(group) || group.length === 0;
    }
    if (!hasDimensions) this.closeDimensions();
  }

  prepareConfig(config) {
    if (!config.interiorEntry) return config;
    config.interiorLock = this.lockedPreset(config.interiorEntry);
    if (config.interiorReverse) {
      config.interiorReverseLock = this.lockedPreset(config.interiorReverse);
    }
    for (const stop of config.guidedTour || []) {
      if (stop.view === 'interior' && stop.camera) stop.camera = this.lockedPreset(stop.camera);
    }
    return config;
  }

  lockedPreset(preset) {
    const [targetX, targetY, targetZ] = preset.target;
    const [theta, phi, radius] = preset.orbit;
    const thetaRadians = theta * (Math.PI / 180);
    const phiRadians = phi * (Math.PI / 180);
    return {
      target: [
        targetX + radius * Math.sin(thetaRadians) * Math.sin(phiRadians),
        targetY + radius * Math.cos(phiRadians),
        targetZ + radius * Math.cos(thetaRadians) * Math.sin(phiRadians),
      ],
      orbit: [theta, phi, 0],
      fov: preset.fov,
    };
  }

  async loadModelViewer() {
    if (!modelViewerLoader) {
      modelViewerLoader = import(this.dataset.modelViewerModuleUrl)
        .then(() => customElements.whenDefined('model-viewer'))
        .catch((error) => {
          modelViewerLoader = undefined;
          throw error;
        });
    }
    return modelViewerLoader;
  }

  waitForLoad(session) {
    return new Promise((resolve, reject) => {
      const viewer = this.#viewer;
      const signal = this.#taskController.signal;
      const cleanup = () => {
        window.clearTimeout(timer);
        viewer.removeEventListener('load', onLoad);
        viewer.removeEventListener('error', onError);
        signal.removeEventListener('abort', onAbort);
      };
      const onLoad = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('3D model load error'));
      };
      const onAbort = () => {
        cleanup();
        reject(new DOMException('Closed', 'AbortError'));
      };
      const timer = window.setTimeout(() => {
        cleanup();
        reject(new Error('3D model load timeout'));
      }, 45000);
      viewer.addEventListener('load', onLoad);
      viewer.addEventListener('error', onError);
      signal.addEventListener('abort', onAbort, { once: true });
      if (!this.isCurrent(session)) {
        onAbort();
      }
    });
  }

  waitForEvent(target, eventName, timeout, session) {
    return new Promise((resolve) => {
      const signal = this.#taskController.signal;
      const cleanup = () => {
        window.clearTimeout(timer);
        target.removeEventListener(eventName, onEvent);
        signal.removeEventListener('abort', onAbort);
      };
      const onEvent = () => {
        cleanup();
        resolve();
      };
      const onAbort = () => {
        cleanup();
        resolve();
      };
      const timer = window.setTimeout(onAbort, timeout);
      target.addEventListener(eventName, onEvent);
      signal.addEventListener('abort', onAbort, { once: true });
      if (!this.isCurrent(session)) {
        onAbort();
      }
    });
  }

  waitForCamera(session) {
    return new Promise((resolve) => {
      const viewer = this.#viewer;
      const signal = this.#taskController.signal;
      let settleTimer;
      const finish = () => {
        window.clearTimeout(settleTimer);
        window.clearTimeout(hardTimer);
        viewer.removeEventListener('camera-change', settle);
        signal.removeEventListener('abort', finish);
        resolve();
      };
      const hardTimer = window.setTimeout(finish, 1200);
      const settle = () => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(finish, 180);
      };
      viewer.addEventListener('camera-change', settle);
      signal.addEventListener('abort', finish, { once: true });
      settle();
      if (!this.isCurrent(session)) {
        finish();
      }
    });
  }

  setState(state) {
    this.#state = state;
    this.dataset.state = state;
    const physicalBusy = ['loading', 'opening', 'entering', 'reversing', 'exiting', 'closing'].includes(state);
    const busy = physicalBusy || this.dataset.guidedState === 'transitioning' || this.hasAttribute('data-technical-loading');
    this.#viewer?.toggleAttribute('camera-controls', !busy && state !== 'error');
    if (['interior', 'reversing'].includes(state) && this.hasAttribute('data-dimensions')) this.closeDimensions();
    for (const button of this.querySelectorAll('[data-3d-action]')) {
      const action = button.dataset['3dAction'];
      let disabled = busy || state === 'error';
      if (action === 'close' || action === 'retry' || action === 'dismiss-info' || action === 'products-close') disabled = false;
      if (action === 'products') disabled = busy;
      if (action === 'open') disabled ||= state !== 'closed' || !this.#config?.animations?.open;
      if (action === 'close-doors') disabled ||= !['open', 'interior'].includes(state) || !this.#config?.animations?.close;
      if (action === 'enter') disabled ||= !['closed', 'open'].includes(state) || !this.#config?.interiorEntry;
      if (action === 'reverse') disabled ||= state !== 'interior';
      if (action === 'exterior') disabled ||= !['open', 'interior'].includes(state);
      if (this.hasAttribute('data-guided') && !['close', 'dismiss-info', 'guided', 'guided-previous', 'guided-next', 'products', 'products-close'].includes(action)) disabled = true;
      if (action === 'guided') disabled = physicalBusy || state === 'error' || this.dataset.guidedState === 'transitioning' || this.guidedTour.length === 0;
      if (action === 'guided-previous') disabled = busy || this.#guidedIndex <= 0;
      if (action === 'guided-next') disabled = busy || this.#guidedIndex < 0 || this.#guidedIndex >= this.guidedTour.length - 1;
      button.disabled = disabled;
    }
    const pickerBlocked = busy;
    if (this.#productSearch) this.#productSearch.disabled = pickerBlocked;
    for (const button of this.#productList?.querySelectorAll('[data-3d-product-handle]') || []) {
      button.disabled = pickerBlocked;
    }
    if (pickerBlocked && this.isProductPickerOpen) this.closeProductPicker(false);
    this.updateGuidedNavigation();
    this.updateHotspots();
    this.scheduleOverlay();
  }

  get isPhysicalBusy() {
    return ['loading', 'opening', 'entering', 'reversing', 'exiting', 'closing'].includes(this.#state) || this.hasAttribute('data-technical-loading');
  }

  handleClose() {
    ++this.#session;
    this.#taskController?.abort();
    this.closeProductPicker(false);
    this.stopOverlayLifecycle();
    this.setState('closed');
    this.teardownViewer();
    this.#lastTrigger?.focus();
  }

  showMessage(message) {
    this.#message.textContent = message;
    this.#message.hidden = false;
  }

  clearMessage() {
    this.#message.textContent = '';
    this.#message.hidden = true;
    this.querySelector('[data-3d-action="retry"]').hidden = true;
  }

  activateBackdrop() {
    const image = this.querySelector('[data-3d-backdrop-image]');
    if (!image || image.getAttribute('src')) return;
    image.setAttribute('srcset', image.dataset.srcset || '');
    image.setAttribute('src', image.dataset.src || '');
  }

  translate(key) {
    const legacyDimensionKey = key
      ?.replace('dimension_exterior_', 'dimension_external_')
      .replace('dimension_interior_', 'dimension_internal_');
    return this.translations[key] || this.translations[legacyDimensionKey] || key || '';
  }

  get translations() {
    try {
      return JSON.parse(this.dataset.translations || '{}');
    } catch {
      return {};
    }
  }

  isCurrent(session) {
    return this.#dialog?.open && this.#session === session;
  }
}

if (!customElements.get('nc-product-3d')) customElements.define('nc-product-3d', NcProduct3d);
